import { useCallback } from 'react';
import axios from 'axios';
import { useAppSelector } from '../store/hooks';
import AsyncSearchSelect, { type SelectOption } from './AsyncSearchSelect';
import type { Student } from '../store/slices/studentsSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

export function formatStudentName(s: {
  firstName?: string | null;
  lastName?: string | null;
  surname?: string | null;
}): string {
  return [s.lastName, s.firstName, s.surname].filter(Boolean).join(' ') || `Student`;
}

export interface ReferrerOption extends SelectOption<number> {
  student: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    surname?: string | null;
    quitDate?: string | null;
  };
}

type Props = {
  value: ReferrerOption | null;
  onChange: (option: ReferrerOption | null) => void;
  excludeStudentId?: number;
  disabled?: boolean;
  placeholder?: string;
};

const StudentReferrerPicker: React.FC<Props> = ({
  value,
  onChange,
  excludeStudentId,
  disabled,
  placeholder = 'Search students (active or quitted)…',
}) => {
  const token = useAppSelector((state) => state.auth?.token);

  const loadOptions = useCallback(async (search: string): Promise<ReferrerOption[]> => {
    const response = await axios.get(`${API_BASE_URL}/students`, {
      params: { search, limit: 40, includeUngrouped: true },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const students = (response.data.students || []) as Student[];
    return students
      .filter((s) => s.id !== excludeStudentId)
      .map((s) => ({
        value: s.id,
        label: formatStudentName(s) + (s.quitDate ? ' (quit)' : ''),
        student: s,
      }));
  }, [token, excludeStudentId]);

  return (
    <AsyncSearchSelect<number, ReferrerOption>
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      placeholder={placeholder}
      isDisabled={disabled}
      noOptionsMessage="No students"
    />
  );
};

export default StudentReferrerPicker;
